import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import { Table, TableBody, TableContainer, TableHead, TableRow, TableSortLabel, Paper } from '@mui/material/';
import { StyledTableCell } from 'components/Common/MUIStyledComponents';
import { Link } from 'react-router-dom';

interface ITableProps {
  cases: any,
  borderRadius: number,
}

interface Data {
  name: string;
  type: string;
  date: number;
  status: string;
  link: string;
}

// CUSTOM TABLE HEADER
function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

type Order = 'asc' | 'desc';

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (a: { [key in Key]: number | string }, b: { [key in Key]: number | string }) => number {
  return order === 'desc' ?
    (a, b) => descendingComparator(a, b, orderBy) :
    (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [ el, index ] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

interface HeadCell {
  id: keyof Data;
  label: string;
}

const headCells: HeadCell[] = [
  { id: 'name', label: 'Name' },
  { id: 'type', label: 'Type' },
  { id: 'date', label: 'Date Created' },
  { id: 'status', label: 'Status' },
  { id: 'link', label: '' },
];

interface EnhancedTableProps {
  classes: any;
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof Data) => void;
  order: Order;
  orderBy: string;
}

const EnhancedTableHead = (props: EnhancedTableProps) => {
  const { classes, order, orderBy, onRequestSort } = props;
  const createSortHandler = (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <StyledTableCell
            key={headCell.id}
            align='left'
            padding='normal'
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <span className={classes.visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </span>
              ) : null}
            </TableSortLabel>
          </StyledTableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

// CUSTOM TABLE ROWS
const Row = styled(TableRow)({
  root: {
    '& > *': {
      borderBottom: 'unset',
    },
    'backgroundColor': '#2e3036',
    '&:hover' : {
      backgroundColor: '#25262b',
      cursor: 'pointer'
    }
  },
});

const completeTag =
  <div className='complete-tag'>
    <p className='props-tag-text'>Complete</p>
  </div>;

const pendingTag =
  <div className='pending-tag'>
    <p className='props-tag-text'>Approval Pending</p>
  </div>;

const draftTag =
  <div className='draft-tag'>
    <p className='props-tag-text'>Draft</p>
  </div>;


const CustomRow = (props: { row: any }) => {
  const { row } = props;

  return (
    <>
      <Row>
        <StyledTableCell component='th' scope='row'>
          {row.ticketName}
        </StyledTableCell>
        <StyledTableCell>
          {row.docType === 'report' ? 'Analytical Report' : row.docType === 'bulkScore' ? 'Bulk Score' : row.docType === 'riskScore' ? 'Risk Score' : 'Daily Report'}
        </StyledTableCell>
        <StyledTableCell>
          {row.date.substr(0, 10)}
        </StyledTableCell>
        <StyledTableCell>
          {row.status === 'complete' ? completeTag : row.status === 'pending' ? pendingTag : draftTag}
        </StyledTableCell>
        <StyledTableCell>
          <Link to={`/analytics-vault/case/${row._id}`}>
              View
          </Link>
        </StyledTableCell>
      </Row>
    </>
  );
};

const PREFIX = 'CaseTable';
const classes = {
  root: `${PREFIX}-root`,
  paper: `${PREFIX}-paper`,
  visuallyHidden: `${PREFIX}-visually-hidden`,
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.root}`]: {
    width: '100%',
  },
  [`& .${classes.paper}`]: {
    width: '100%',
    background: '#2E3036',
  },
  [`& .${classes.visuallyHidden}`]: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
}));

const CaseTable: React.FC<ITableProps> = (props: ITableProps) => {
  const { cases, borderRadius } = props;

  const [ order, setOrder ] = useState<Order>('asc');
  const [ orderBy, setOrderBy ] = useState<keyof Data>('name');

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof Data) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <>
      <Root className={classes.root}>
        <Paper className={classes.paper}>
          <TableContainer style={{ borderRadius: `${borderRadius}px` }}>
            <Table
              aria-labelledby='tableTitle'
              aria-label='enhanced table'
            >
              <EnhancedTableHead
                classes={classes}
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {stableSort(cases, getComparator(order, orderBy)).map((row: any, index: number) => {
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <CustomRow row={row} key={index}/>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Root>
    </>
  );
};

export default CaseTable;


